import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CheckCircle2 } from "lucide-react";
import { DAYS, imgUrl } from "@/lib/api";

export const Kente = ({ className = "kente-band" }) => <div aria-hidden className={`kente ${className} w-full`} />;

export const SectionHeading = ({ eyebrow, title, accent, children, id }) => (
  <div className="mb-10 max-w-2xl" id={id}>
    {eyebrow && (
      <p className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
        <span className="h-px w-8 bg-emerald-600" />
        {eyebrow}
      </p>
    )}
    <h2 className="font-display text-4xl font-bold leading-[0.95] text-gold sm:text-5xl">
      {title} {accent && <span className="font-script normal-case tracking-normal text-red-500">{accent}</span>}
    </h2>
    {children && <p className="mt-4 text-sm leading-relaxed text-neutral-400 md:text-base">{children}</p>}
  </div>
);

export const Field = ({ label, id, error, hint, children, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-widest text-neutral-300">
      {label}
    </Label>
    {children || <Input id={id} className="field-input" {...props} />}
    {hint && !error && <p className="text-xs text-neutral-500">{hint}</p>}
    {error && <p className="text-xs text-red-400">{error}</p>}
  </div>
);

export const DayChips = ({ value, onChange, schedule, prefix }) => {
  const labels = Object.fromEntries((schedule || []).map((s) => [s.day, s]));
  const toggle = (d) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d]);
  return (
    <div className="grid grid-cols-2 gap-2">
      {DAYS.map((d) => {
        const on = value.includes(d);
        return (
          <button
            type="button"
            key={d}
            aria-pressed={on}
            data-testid={`${prefix}-day-${d}`}
            onClick={() => toggle(d)}
            className={`rounded-md border px-3 py-3 text-left transition-colors duration-200 ${
              on ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-600"
            }`}
          >
            <span className="block font-display text-sm">{labels[d]?.date_label || d}</span>
            <span className="block truncate text-xs opacity-80">{labels[d]?.title}</span>
          </button>
        );
      })}
    </div>
  );
};

export const SuccessCard = ({ title, children, onReset, testid }) => (
  <div data-testid={testid} className="rise rounded-lg border border-emerald-700/60 bg-emerald-950/30 p-8">
    <CheckCircle2 className="mb-4 h-10 w-10 text-emerald-400" />
    <h3 className="font-display text-2xl text-gold">{title}</h3>
    <div className="mt-3 text-sm leading-relaxed text-neutral-300">{children}</div>
    {onReset && (
      <button onClick={onReset} className="mt-6 text-sm font-semibold text-amber-400 underline-offset-4 hover:underline" data-testid={`${testid}-reset`}>
        Submit another
      </button>
    )}
  </div>
);

export const FormCard = ({ children, className = "" }) => (
  <div className={`relative overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/80 ${className}`}>
    <Kente className="kente-thin" />
    <div className="p-5 sm:p-8">{children}</div>
  </div>
);

export const SubmitButton = ({ loading, children, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="flex h-14 w-full items-center justify-center rounded-full bg-amber-500 px-8 font-display text-lg font-semibold text-neutral-950 shadow-[0_0_24px_rgba(245,158,11,0.25)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-amber-400 active:translate-y-0 disabled:opacity-60"
  >
    {loading ? "Sending…" : children}
  </button>
);

export const LogoBadge = ({ content, className = "", testid }) => {
  const logo = content.images?.logo;
  if (!logo) return null;
  return (
    <span className={`inline-flex shrink-0 items-center justify-center overflow-hidden bg-[#fdf8ee] ring-1 ring-amber-500/40 ${className}`} data-testid={testid}>
      <img src={imgUrl(logo.url)} alt="Ilesa Ire logo" className="h-full w-full object-contain" />
    </span>
  );
};
